import {Check, ChevronsUpDown} from "lucide-react";
import * as React from "react";

import {Button} from "@/components/ui/button";
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from "@/components/ui/dropdown-menu";
import DateRangePicker from "@/lib/filters/date/date-range-picker";
import {areDateRangesEqual, getRangeSelectShortcutButtonsShortList} from "@/lib/filters/date/date-utils";
import {DateShortcut, PresetProps} from "@/lib/filters/date/types";
import {cn} from "@/lib/utils";
import {useDate} from "@/stores/date-filter";

function PresetsDropdown({
  presetSelected,
  buttonShortlist,
  setDate
}: PresetProps) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant={'ghost'}
          className={cn("data-[state=open]:bg-blue-200 data-[state=open]:text-primary text-[10px] leading-[10px] h-6 lg:text-xs gap-0 px-2 py-0 lg:h-8",
            presetSelected && "bg-blue-200 text-primary"
          )}
        >
          {presetSelected?.label ?? 'Select Range' }
          <ChevronsUpDown className="ml-auto size-4"/>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] rounded-md overflow-scroll max-h-[80vh]"
        side='top'
        align="end"
        sideOffset={4}
      >
        {buttonShortlist.map(preset  => {
          return(
            <DropdownMenuItem
              key={preset.label}
              onClick={() => setDate(preset.date)}
              className={`hover:cursor-pointer ${presetSelected?.label === preset.label ? 'text-secondary' : ''}`}
            >
              <span className={'flex items-center gap-2'}>{preset.label} {presetSelected?.label === preset.label && <Check size={16} />}</span>
            </DropdownMenuItem>
          )})}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function PresetButtons({
  presetSelected,
  buttonShortlist,
  setDate
}: PresetProps) {
  return (
    <>
      { buttonShortlist.map(preset => {
        return(
          <Button
            onClick={() => setDate(preset.date)}
            variant={'ghost'}
            key={`presets-${preset.label}`}
            data-testid={`date-filter:presets:${preset.label}`}
            className={cn(
              "text-[10px] leading-[10px] h-6 lg:text-xs gap-0 px-1 lg:px-2 py-0 lg:h-8",
              presetSelected?.label === preset.label && "hover:bg-blue-200 bg-blue-200",
            )}
          >
            {preset.label}
          </Button>
        )
      })}
    </>
  )
}

export default function DateFilter() {
  const { setDate, date } = useDate();
  const [presetSelected, setPresetSelected] = React.useState<DateShortcut | undefined>(undefined)
  const buttonShortlist = getRangeSelectShortcutButtonsShortList()
  React.useEffect(() => {
    setPresetSelected(buttonShortlist.find(preset => areDateRangesEqual(date, preset.date)))
  }, [date, buttonShortlist])
  return (
    <div className={'flex gap-0 justify-end bg-white rounded-md p-1 box-border'}>
      <div className={'hidden lg:flex justify-end'}><PresetButtons buttonShortlist={buttonShortlist} presetSelected={presetSelected} setDate={setDate} /></div>
      <div className={'flex justify-end lg:hidden'}><PresetsDropdown buttonShortlist={buttonShortlist} presetSelected={presetSelected} setDate={setDate} /></div>
      <DateRangePicker
        label={presetSelected ? 'Custom' : ''}
        triggerButtonClassName={cn(
          'text-[10px] leading-[10px] h-6 lg:text-xs gap-0 px-1 lg:px-2 py-0 lg:h-8',
          date && !presetSelected && "bg-blue-200 text-primary"
        )}
      />
    </div>
  )
}